import React, { useState } from 'react';
import Card from '../components/common/Card';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import useStableford from '../hooks/useStableford';

export default function StablefordCalculator() {
  const { calculatePoints, getScoreDescription, getScoreColor } = useStableford();

  const [formData, setFormData] = useState({
    strokes: 5,
    par: 4,
    strokeIndex: 10,
    handicap: 12
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Pre-defined scenarios for quick testing
  const scenarios = [
    {
      name: 'Scratch Player Par',
      data: { strokes: 4, par: 4, strokeIndex: 1, handicap: 0 },
      description: 'A scratch golfer making par'
    },
    {
      name: 'Handicap 18 Net Birdie',
      data: { strokes: 4, par: 4, strokeIndex: 5, handicap: 18 },
      description: '18 handicapper getting 1 stroke, making net birdie'
    },
    {
      name: 'Handicap 9 on Hard Hole',
      data: { strokes: 5, par: 4, strokeIndex: 3, handicap: 9 },
      description: '9 handicapper with stroke on SI 3'
    },
    {
      name: 'High Handicapper Par 5',
      data: { strokes: 7, par: 5, strokeIndex: 1, handicap: 20 },
      description: '20 handicapper on par 5 (gets 2 strokes)'
    },
    {
      name: 'Disaster Hole',
      data: { strokes: 8, par: 4, strokeIndex: 10, handicap: 12 },
      description: 'High score on a hole'
    }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: parseInt(value) || 0
    }));
    setError('');
  };

  const handleCalculate = () => {
    try {
      const calculationResult = calculatePoints(
        formData.strokes,
        formData.par,
        formData.strokeIndex,
        formData.handicap
      );

      setResult({
        ...calculationResult,
        description: getScoreDescription(calculationResult.scoreToPar),
        colorClass: getScoreColor(calculationResult.scoreToPar)
      });
      setError('');
    } catch (err) {
      setError(err.message);
      setResult(null);
    }
  };

  const loadScenario = (scenario) => {
    setFormData(scenario.data);
    setError('');

    // Auto-calculate after loading
    setTimeout(() => {
      try {
        const calculationResult = calculatePoints(
          scenario.data.strokes,
          scenario.data.par,
          scenario.data.strokeIndex,
          scenario.data.handicap
        );

        setResult({
          ...calculationResult,
          description: getScoreDescription(calculationResult.scoreToPar),
          colorClass: getScoreColor(calculationResult.scoreToPar)
        });
      } catch (err) {
        setError(err.message);
      }
    }, 100);
  };

  return (
    <div className="px-4">
      <h1 className="text-4xl font-bold text-gray-900 mb-4">
        Stableford Calculator
      </h1>
      <p className="text-lg text-gray-600 mb-6">
        Test the Stableford scoring system with different scenarios
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calculator Form */}
        <Card title="Enter Scenario">
          <div className="space-y-4">
            <Input
              label="Strokes Taken"
              name="strokes"
              type="number"
              min="1"
              max="15"
              value={formData.strokes}
              onChange={handleChange}
              required
            />

            <div>
              <label className="block text-base font-semibold text-gray-700 mb-2">
                Par <span className="text-error">*</span>
              </label>
              <select
                name="par"
                value={formData.par}
                onChange={handleChange}
                className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="3">3 (Par 3)</option>
                <option value="4">4 (Par 4)</option>
                <option value="5">5 (Par 5)</option>
              </select>
            </div>

            <Input
              label="Stroke Index"
              name="strokeIndex"
              type="number"
              min="1"
              max="18"
              value={formData.strokeIndex}
              onChange={handleChange}
              required
            />

            <Input
              label="Player Handicap"
              name="handicap"
              type="number"
              min="0"
              max="20"
              value={formData.handicap}
              onChange={handleChange}
              required
            />

            {error && (
              <div className="bg-error text-white px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <Button
              variant="primary"
              onClick={handleCalculate}
              className="w-full"
            >
              Calculate Points
            </Button>
          </div>
        </Card>

        {/* Result Display */}
        <Card title="Calculation Result">
          {result ? (
            <div className="space-y-4">
              {/* Points Display */}
              <div className={`text-center p-8 rounded-lg ${result.colorClass} border-2 border-gray-200`}>
                <div className="text-6xl font-bold mb-2">
                  {result.points}
                </div>
                <div className="text-xl font-semibold">
                  {result.points === 1 ? 'Point' : 'Points'}
                </div>
              </div>

              {/* Breakdown */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="font-semibold">Score Description:</span>
                  <span className="font-bold text-primary">{result.description}</span>
                </div>

                <div className="flex justify-between">
                  <span className="font-semibold">Gross Score:</span>
                  <span>{formData.strokes}</span>
                </div>

                <div className="flex justify-between">
                  <span className="font-semibold">Strokes Received:</span>
                  <span>{result.strokesReceived}</span>
                </div>

                <div className="flex justify-between border-t pt-3">
                  <span className="font-semibold">Net Score:</span>
                  <span className="font-bold">{result.netScore}</span>
                </div>

                <div className="flex justify-between">
                  <span className="font-semibold">Par:</span>
                  <span>{formData.par}</span>
                </div>

                <div className="flex justify-between border-t pt-3">
                  <span className="font-semibold">Score to Par:</span>
                  <span className="font-bold">
                    {result.scoreToPar > 0 ? `+${result.scoreToPar}` : result.scoreToPar}
                  </span>
                </div>
              </div>

              {/* Explanation */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <h4 className="font-bold text-blue-900 mb-2">How it works:</h4>
                <ol className="text-sm text-blue-800 space-y-1">
                  <li>1. Handicap {formData.handicap} on SI {formData.strokeIndex} = {result.strokesReceived} stroke{result.strokesReceived !== 1 ? 's' : ''}</li>
                  <li>2. Net Score = {formData.strokes} (gross) - {result.strokesReceived} (strokes) = {result.netScore}</li>
                  <li>3. Score to Par = {result.netScore} (net) - {formData.par} (par) = {result.scoreToPar}</li>
                  <li>4. {result.description} = {result.points} point{result.points !== 1 ? 's' : ''}</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">Enter values and click Calculate</p>
              <p className="text-sm mt-2">or try a scenario below</p>
            </div>
          )}
        </Card>
      </div>

      {/* Quick Scenarios */}
      <Card title="Quick Test Scenarios" className="mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scenarios.map((scenario, index) => (
            <button
              key={index}
              onClick={() => loadScenario(scenario)}
              className="bg-gray-50 hover:bg-gray-100 border-2 border-gray-200 rounded-lg p-4 text-left transition-colors"
            >
              <h4 className="font-bold text-gray-900 mb-2">{scenario.name}</h4>
              <p className="text-sm text-gray-600 mb-3">{scenario.description}</p>
              <div className="text-xs text-gray-500 space-y-1">
                <div>Strokes: {scenario.data.strokes} | Par: {scenario.data.par}</div>
                <div>SI: {scenario.data.strokeIndex} | HCP: {scenario.data.handicap}</div>
              </div>
            </button>
          ))}
        </div>
      </Card>

      {/* Stableford Points Reference */}
      <Card title="Stableford Points Reference" className="mt-6">
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Score (Net)</th>
                <th className="px-4 py-3 text-left font-bold">Description</th>
                <th className="px-4 py-3 text-center font-bold">Points</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr className="bg-blue-50">
                <td className="px-4 py-3">2+ under par</td>
                <td className="px-4 py-3">Eagle / Albatross</td>
                <td className="px-4 py-3 text-center font-bold text-2xl">4</td>
              </tr>
              <tr className="bg-green-50">
                <td className="px-4 py-3">1 under par</td>
                <td className="px-4 py-3">Birdie</td>
                <td className="px-4 py-3 text-center font-bold text-2xl">3</td>
              </tr>
              <tr className="bg-white">
                <td className="px-4 py-3">Par</td>
                <td className="px-4 py-3">Par</td>
                <td className="px-4 py-3 text-center font-bold text-2xl">2</td>
              </tr>
              <tr className="bg-yellow-50">
                <td className="px-4 py-3">1 over par</td>
                <td className="px-4 py-3">Bogey</td>
                <td className="px-4 py-3 text-center font-bold text-2xl">1</td>
              </tr>
              <tr className="bg-red-50">
                <td className="px-4 py-3">2+ over par</td>
                <td className="px-4 py-3">Double Bogey+</td>
                <td className="px-4 py-3 text-center font-bold text-2xl">0</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
